import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { _HttpClient } from '@delon/theme';
import { DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzTableModule } from 'ng-zorro-antd/table';
import { finalize } from 'rxjs';

import { SaveLayoutComponent } from './save-layout.component';

@Component({
  selector: 'app-layout-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzCheckboxModule,
    NzDividerModule,
    NzIconModule,
    NzInputModule,
    NzModalModule,
    NzTableModule,
    NzGridModule,
    DxDataGridModule
  ],
  template: `
    <nz-modal
      [(nzVisible)]="visible"
      [nzTitle]="'Manage Layouts'"
      [nzWidth]="800"
      [nzFooter]="null"
      (nzOnCancel)="close()">
      <ng-container *nzModalContent>
        <div class="search-container mb-md">
          <nz-input-group [nzSuffix]="suffixIcon">
            <input
              type="text"
              nz-input
              placeholder="Search layouts"
              [(ngModel)]="searchText"
              (ngModelChange)="onSearch()"
            />
          </nz-input-group>
          <ng-template #suffixIcon>
            <span nz-icon nzType="search"></span>
          </ng-template>
        </div>

        <dx-data-grid
          [dataSource]="filteredLayouts"
          [showBorders]="true"
          [showColumnLines]="true"
          [showRowLines]="true"
          [columnAutoWidth]="true"
          [hoverStateEnabled]="true"
        >
          <dxi-column dataField="layoutName" caption="Layout Name" [headerCellTemplate]="'headerTemplate'"></dxi-column>
          <dxi-column dataField="isDefault" caption="Default" [headerCellTemplate]="'headerTemplate'" cellTemplate="defaultTemplate" width="100"></dxi-column>
          <dxi-column caption="" cellTemplate="actionsTemplate" width="150" alignment="right"></dxi-column>

          <div *dxTemplate="let cell of 'headerTemplate'">
            <div class="grid-header">{{ cell.column.caption }}</div>
          </div>

          <div *dxTemplate="let cell of 'defaultTemplate'">
            <label
              nz-checkbox
              [(ngModel)]="cell.data.isDefault"
              (ngModelChange)="onDefaultChange(cell.data)"
            ></label>
          </div>

          <div *dxTemplate="let cell of 'actionsTemplate'">
            <a class="action-link" (click)="editLayout(cell.data)">Edit</a>
            <a class="action-link delete" (click)="deleteLayout(cell.data)">Delete</a>
          </div>
        </dx-data-grid>

        <nz-row nzJustify="end" class="mt-md">
          <nz-col>
            <button nz-button nzType="default" (click)="close()">Close</button>
          </nz-col>
        </nz-row>
      </ng-container>
    </nz-modal>
  `,
  styles: [`
    :host {
      display: block;
    }
    .search-container {
      margin-bottom: 16px;
    }
    .action-link {
      color: #1890ff;
      cursor: pointer;
      margin-left: 16px;
    }
    .action-link.delete {
      color: #ff4d4f;
    }
    ::ng-deep .dx-datagrid {
      background-color: #fff;
    }
    ::ng-deep .dx-datagrid-headers {
      background-color: #00174f !important;
      color: #fff;
    }
    ::ng-deep .dx-datagrid-headers .dx-datagrid-text-content {
      color: #fff;
    }
    .grid-header {
      color: #fff;
      font-weight: 500;
    }
    ::ng-deep .dx-datagrid-rowsview {
      background-color: #fff;
    }
    ::ng-deep .dx-datagrid-rowsview .dx-row > td {
      padding: 16px;
      background-color: #fff;
    }
    ::ng-deep .dx-datagrid-borders > .dx-datagrid-headers,
    ::ng-deep .dx-datagrid-borders > .dx-datagrid-rowsview,
    ::ng-deep .dx-datagrid-borders > .dx-datagrid-total-footer {
      border-left: 1px solid #e8e8e8;
      border-right: 1px solid #e8e8e8;
    }
    .mt-md {
      margin-top: 24px;
    }
    .mb-md {
      margin-bottom: 24px;
    }
    ::ng-deep .ant-modal-header {
      border-radius: 1px 1px 0 0;
      background-color: #08306B;
      border-bottom: 1px solid #08306B;
    }
    ::ng-deep .ant-modal-title {
      color: #fff !important;
    }
    ::ng-deep .ant-modal-close {
      color: #fff !important;
    }
    ::ng-deep .ant-modal-close:hover {
      color: rgba(255, 255, 255, 0.85);
    }
  `]
})
export class LayoutManagementComponent {
  private modal = inject(NzModalRef);
  private modalService = inject(NzModalService);
  private http = inject(_HttpClient);
  private msg = inject(NzMessageService);

  visible = true;
  searchText: string = '';
  layouts: Array<{ id: number; layoutName: string; layout: any; isDefault?: boolean; componentName: string }> = [];
  filteredLayouts: Array<{ id: number; layoutName: string; layout: any; isDefault?: boolean; componentName: string }> = [];
  loading = false;

  constructor() {
    this.layouts = this.modal.getConfig().nzData?.layouts || [];
    this.filteredLayouts = [...this.layouts];
  }

  onSearch(): void {
    if (!this.searchText) {
      this.filteredLayouts = [...this.layouts];
    } else {
      this.filteredLayouts = this.layouts.filter(layout =>
        layout.layoutName.toLowerCase().includes(this.searchText.toLowerCase())
      );
    }
  }

  onDefaultChange(layout: any): void {
    if (!layout.isDefault) {
      // If unchecking, don't allow - must have one default
      layout.isDefault = true;
      return;
    }

    this.loading = true;
    this.http.put(`api/gridlayout/${layout.id}/default`, { componentName: layout.componentName })
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            // Update local state
            this.layouts.forEach(l => {
              l.isDefault = l.id === layout.id;
            });
            this.msg.success('Default layout updated successfully');
          } else {
            // Revert change if API call failed
            layout.isDefault = false;
            this.msg.error(res.message || 'Failed to update default layout');
          }
        },
        error: (err) => {
          // Revert change if API call failed
          layout.isDefault = false;
          this.msg.error('Failed to update default layout');
          console.error('Error updating default layout:', err);
        }
      });
  }

  editLayout(layout: any): void {
    const modalRef = this.modalService.create({
      nzContent: SaveLayoutComponent,
      nzFooter: null,
      nzTitle: layout.layoutName ? 'Edit Layout' : 'Save New Layout',
      nzWidth: 400,
      nzCentered: true,
      nzClosable: false,
      nzMaskClosable: false,
      nzData: {
        layoutName: layout.layoutName,
        isDefault: layout.isDefault
      }
    });

    modalRef.afterClose.subscribe((result) => {
      if (result) {
        this.loading = true;
        const updateData = {
          id: layout.id,
          componentName: layout.componentName,
          layoutName: result.name,
          layout: layout.layout,
          isDefault: result.isDefault
        };

        this.http.post('api/gridlayout', updateData)
          .pipe(finalize(() => this.loading = false))
          .subscribe({
            next: (res: any) => {
              if (res.id > 0) {
                // Update local state
                layout.layoutName = result.name;
                layout.isDefault = result.isDefault;

                // If this is set as default, remove default from other layouts
                if (result.isDefault) {
                  this.layouts.forEach(l => {
                    if (l.id !== layout.id) {
                      l.isDefault = false;
                    }
                  });
                }

                this.onSearch();
              }
            },
            error: (err) => {
              this.msg.error('Failed to update layout');
              console.error('Error updating layout:', err);
            }
          });
      }
    });
  }

  deleteLayout(layout: any): void {
    this.modalService.confirm({
      nzTitle: 'Delete Layout',
      nzContent: `Are you sure you want to delete the layout "${layout.layoutName}"?`,
      nzOkText: 'Yes',
      nzOkDanger: true,
      nzOnOk: () => {
        this.loading = true;
        this.http.delete(`api/gridlayout/${layout.id}`)
          .pipe(finalize(() => this.loading = false))
          .subscribe({
            next: (res: any) => {
              if (res.success) {
                const index = this.layouts.indexOf(layout);
                if (index > -1) {
                  this.layouts.splice(index, 1);
                  this.onSearch();
                }
              }
            },
            error: (err) => {
              this.msg.error('Failed to delete layout');
              console.error('Error deleting layout:', err);
            }
          });
      }
    });
  }

  close(): void {
    this.visible = false;
    setTimeout(() => {
      this.modal.close(this.layouts);
    }, 200);
  }
}
